/**
 * Conversion check 표시 정본.
 *
 * FCV 정본(View R UI `2886:144777`)은 같은 판정을 Conversion report 모달과
 * Library › Object Details 두 자리에서 보여준다. "문구는 같아야 한다"가 설계
 * 조건이라 행 생성 규칙과 문구를 한 곳에서 만든다. 행은 문제가 있을 때만
 * 만들고 순서는 Materials 다음 Textures로 고정한다.
 */

export type ConversionCheckTone =
  | "no_issues"
  | "needs_review"
  | "not_checked"
  | "failed";

export interface ConversionCheckRow {
  label: string;
  value?: string;
  detail?: string;
  tone: ConversionCheckTone;
  successDot?: boolean;
}

export interface ConversionCheckPresentation {
  chipLabel?: string;
  rows: ConversionCheckRow[];
  status: ConversionCheckTone;
}

interface ConversionCheckGroupInput {
  state: "no_issues" | "needs_review" | "not_checked";
  replaced_count?: number;
  missing_count?: number;
}

/** Main API projection과 Library 상세 응답이 공유하는 최소 구조. */
export interface ConversionCheckInput {
  quality_state: "no_issues" | "needs_review" | "not_checked" | "failed";
  converter_outcome: "success" | "warning" | "failed";
  loader_outcome: "not_checked" | "success" | "failed";
  materials: ConversionCheckGroupInput;
  textures: ConversionCheckGroupInput;
  warning_codes: string[];
}

const NOT_CHECKED_PRESENTATION: ConversionCheckPresentation = {
  chipLabel: "Not checked",
  rows: [
    {
      label: "Materials",
      value: "Not checked",
      detail: "Materials could not be checked, but this object opens normally.",
      tone: "not_checked",
    },
  ],
  status: "not_checked",
};

export const conversionDisplayState = (
  conversionCheck: ConversionCheckInput | null,
): ConversionCheckTone | null => {
  if (!conversionCheck) return null;
  if (
    conversionCheck.quality_state === "failed" ||
    conversionCheck.converter_outcome === "failed" ||
    conversionCheck.loader_outcome === "failed"
  ) {
    return "failed";
  }
  if (
    conversionCheck.quality_state === "not_checked" ||
    conversionCheck.loader_outcome === "not_checked" ||
    conversionCheck.materials.state === "not_checked" ||
    conversionCheck.textures.state === "not_checked"
  ) {
    return "not_checked";
  }
  return conversionCheck.quality_state;
};

export const conversionCheckPresentation = (
  conversionCheck: ConversionCheckInput | null,
): ConversionCheckPresentation => {
  if (!conversionCheck) return NOT_CHECKED_PRESENTATION;

  const displayState = conversionDisplayState(conversionCheck);
  if (displayState === "failed") {
    return {
      rows: [
        {
          label: "This object could not be opened.",
          value: "Failed",
          tone: "failed",
        },
      ],
      status: "failed",
    };
  }
  if (displayState === "not_checked") return NOT_CHECKED_PRESENTATION;

  if (conversionCheck.quality_state === "no_issues") {
    return {
      rows: [
        {
          label: "All parts, materials and textures came through.",
          tone: "no_issues",
          successDot: true,
        },
      ],
      status: "no_issues",
    };
  }

  const rows: ConversionCheckRow[] = [];
  const replacedCount = conversionCheck.materials.replaced_count ?? 0;
  if (
    conversionCheck.materials.state === "needs_review" ||
    replacedCount > 0 ||
    conversionCheck.warning_codes.includes("DEFAULT_MATERIAL_APPLIED")
  ) {
    rows.push({
      label: "Materials",
      value: replacedCount > 0 ? `${replacedCount} replaced` : "Needs review",
      detail:
        "This object had no material, so a default gray material was applied.",
      tone: "needs_review",
    });
  }

  const missingCount = conversionCheck.textures.missing_count ?? 0;
  if (
    conversionCheck.textures.state === "needs_review" ||
    missingCount > 0 ||
    conversionCheck.warning_codes.includes("EXTERNAL_TEXTURE_MISSING")
  ) {
    rows.push({
      label: "Textures",
      value: missingCount > 0 ? `${missingCount} missing` : "Needs review",
      detail: "Some textures were not found in the uploaded package.",
      tone: "needs_review",
    });
  }

  return { chipLabel: "Needs review", rows, status: "needs_review" };
};
